/**
 * Phase1Panel.tsx — FEAT-2026-0007 6B.4 / 6F.4
 * Phase-1 (Machine planning) body for the Scenario wizard.
 * Row rendering → Phase1TaskRow.tsx; bulk actions → ScenarioBulkActionBar.tsx.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { PHASE1_TASK_COLUMNS } from './phase1TaskColumns';
import { Phase1TaskRow } from './Phase1TaskRow';
import { Phase1TaskGroup } from './Phase1TaskGroup';
import { Phase1PrepGroupRow } from './Phase1PrepGroupRow';
import { rollupPrepTasks } from './prepRollup';
import { Phase1MachineGrid } from './Phase1MachineGrid';
import { useScheduleHolidays } from '../../../../hooks/useScheduleHolidays';
import { Phase1BulkActionBar } from './ScenarioBulkActionBar';
import { SchedulingConflictsPanel } from '../SchedulingConflictsPanel';
import {
  changeBookingMachine,
  deleteTask,
  listTasksForSource,
  placeTaskOnMachine,
  setBookingStartDate,
  setBookingQty,
  updateTask,
  type TaskWithBookings,
} from '../../../../services/scheduleTasks';
import { AddTaskDialog } from './AddTaskDialog';
import { AutoScheduleControl } from './AutoScheduleControl';
import { UnmappedTestsChip } from './UnmappedTestsChip';
import { useFlashBooking } from './useFlashBooking';
import { emitScheduleChange } from '../../../../services/scheduleBus';
import { SCHEDULE_CONTENT_PADDING } from '../scheduleGridShared';
import type { Scenario } from '../../../../services/scheduleScenario';
import type { ConflictSummary } from '../../../../types/schedule';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Phase1PanelProps {
  compareToBaseline: boolean;
  scenario: Scenario | null;
  /** Reports unplaced + hard-conflict counts to ScenarioPhaseStepper gate (6B.7). */
  onPhase1Stats?: (unplaced: number, hardConflicts: number) => void;
  /** 6D.2 — incremented on phase-transition-back to trigger a data refresh. */
  onEnterRefreshKey?: number;
  /**
   * Index-only skip (2026-08-04): reports the TOTAL machine-task count after each
   * completed load — the stepper auto-advances to Phase 2 (Index tests) when a
   * plan has zero machine tasks but does have index allocations, so users don't
   * land on an empty machine-planning page.
   */
  onTasksLoaded?: (total: number) => void;
  /** True when the source has index allocations — tunes the empty-state copy. */
  hasIndexTests?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase1Panel: React.FC<Phase1PanelProps> = ({
  compareToBaseline,
  scenario,
  onPhase1Stats,
  onEnterRefreshKey = 0,
  onTasksLoaded,
  hasIndexTests = false,
}) => {
  const theme = useTheme();

  // Derive source info from the scenario
  const source: 'Quote' | 'WorkOrder' | null = scenario?.SourceType ?? null;
  const sourceKey: string | null =
    source === 'Quote' ? (scenario?.QuoteKey ?? null) : (scenario?.WorkOrderKey ?? null);
  const scenarioKey: string | null = scenario?.ScenarioKey ?? null;

  // ── State ──────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<TaskWithBookings[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveFlash, setSaveFlash] = useState<string | null>(null);

  // Conflicts from Phase1MachineGrid (6B.6)
  const [conflicts, setConflicts] = useState<ConflictSummary>({ hard: [], soft: [] });
  // Ref to grid container Box — queried by handleJumpToBooking
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // Incremented on task-table mutation to trigger grid refetch
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const bumpGrid = () => setGridRefreshKey((k) => k + 1);
  // Edit Multiple: checkbox-select mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // FEAT-2026-0007 conflict-tag fix: map each task → suggested earliest-free start
  // from the LOADED hard conflicts, so every conflicting row shows the red outline
  // + "Use <date>" hint immediately — not only after the user edits that row.
  const conflictSuggestionByTask = useMemo(() => {
    const bookingToSuggestion = new Map<string, string>();
    for (const h of conflicts.hard) {
      for (const b of h.bookings) {
        if (b.suggestedStart) bookingToSuggestion.set(b.key, b.suggestedStart);
      }
    }
    const byTask = new Map<string, string>();
    for (const t of tasks) {
      for (const bk of t.Bookings) {
        const s = bookingToSuggestion.get(bk.MachineBookingKey);
        if (s && !byTask.has(t.ScheduleTaskKey)) byTask.set(t.ScheduleTaskKey, s);
      }
    }
    return byTask;
  }, [conflicts, tasks]);

  // ── Data loading ───────────────────────────────────────────────────────────

  // Ref so loadTasks doesn't re-create (and re-fire its mount effect) when the
  // parent's callback identity changes.
  const onTasksLoadedRef = useRef(onTasksLoaded);
  useEffect(() => { onTasksLoadedRef.current = onTasksLoaded; }, [onTasksLoaded]);

  const loadTasks = useCallback(async () => {
    if (!source || !sourceKey) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
      setTasks(data);
      // Only report after a COMPLETED load — the initial empty state must not
      // read as "this plan has zero machine tasks" (index-only skip, 2026-08-04).
      onTasksLoadedRef.current?.(data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [source, sourceKey, scenarioKey]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  // 6D.2 — on-enter refresh (skip value 0 = initial mount)
  const onEnterRefreshKeyRef = useRef(0);
  useEffect(() => {
    if (onEnterRefreshKey === 0) return;
    if (onEnterRefreshKey === onEnterRefreshKeyRef.current) return;
    onEnterRefreshKeyRef.current = onEnterRefreshKey;
    void loadTasks();
    bumpGrid();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEnterRefreshKey, loadTasks]);

  // Auto-dismiss the save flash banner after 2.5 s
  useEffect(() => {
    if (!saveFlash) return;
    const id = window.setTimeout(() => setSaveFlash(null), 2500);
    return () => window.clearTimeout(id);
  }, [saveFlash]);

  // Report unplaced + hard-conflict counts to the stepper guard (6B.7).
  useEffect(() => {
    if (!onPhase1Stats) return;
    const unplaced = tasks.filter((t) => t.Bookings.length === 0).length;
    onPhase1Stats(unplaced, conflicts.hard.length);
  }, [tasks, conflicts.hard.length, onPhase1Stats]);

  // ── Mutation handlers (passed down to Phase1TaskRow) ───────────────────────

  const handleUpdateMachineCount = useCallback(
    async (taskKey: string, count: number) => {
      // Pass the scenario being edited so the backend reconciles THIS plan's bookings
      // (a duplicated plan otherwise reconciled the original and the split never appeared).
      await updateTask(taskKey, { MachineCount: count, ...(scenarioKey ? { ScenarioKey: scenarioKey } : {}) });
      // BUG (2026-06-20): updateTask returns the task via a NON-scenario-scoped
      // fetch, so a draft scenario's tentative bookings are filtered out — after
      // reducing the count the task looked "unplaced" ("Pick a machine"), and
      // re-picking created a DUPLICATE booking + a count/bookings mismatch.
      // Refetch the list scoped to THIS scenario so the surviving booking shows.
      if (source && sourceKey) {
        const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
        setTasks(data);
      }
      bumpGrid();
      emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, sourceKey, scenarioKey],
  );

  const handleChangeMachine = useCallback(
    async (bookingKey: string, machineKey: string) => {
      await changeBookingMachine(bookingKey, machineKey);
      if (source && sourceKey) {
        const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
        setTasks(data);
      }
      bumpGrid();
      emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, sourceKey, scenarioKey],
  );

  const handleChangeBookingStart = useCallback(
    async (
      bookingKey: string,
      startDate: string,
      opts?: { force?: boolean },
    ): Promise<
      | { kind: 'updated' }
      | { kind: 'needsConfirm'; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null }
    > => {
      const result = await setBookingStartDate(bookingKey, startDate, opts);
      if (result.kind === 'updated') {
        if (source && sourceKey) {
          const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
          setTasks(data);
        }
        bumpGrid();
        // Tell the cache + the other views the booking dates moved (scenario-phase1
        // source so Phase-2 reloads its tasks; capacityCache flushes regardless).
        emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
        return { kind: 'updated' };
      }
      return {
        kind: 'needsConfirm',
        conflict: result.conflict,
        weekend: result.weekend,
        earliestFree: result.earliestFree,
        conflictLabel: result.conflictLabel ?? null,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, sourceKey, scenarioKey],
  );

  const handleChangeBookingQty = useCallback(
    async (
      bookingKey: string,
      qty: number,
      opts?: { force?: boolean },
    ): Promise<{ kind: 'updated' } | { kind: 'needsConfirm'; conflictLabel?: string | null }> => {
      try {
        const result = await setBookingQty(bookingKey, qty, opts);
        if (result.kind === 'updated') {
          if (source && sourceKey) {
            const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
            setTasks(data);
          }
          bumpGrid();
          emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
          return { kind: 'updated' };
        }
        return { kind: 'needsConfirm', conflictLabel: result.conflictLabel ?? null };
      } catch (err) {
        // Surface the failure (e.g. 400 out-of-range, 500, network) instead of
        // swallowing it — the row's fire-and-forget commit can't show it otherwise.
        setError(err instanceof Error ? err.message : 'Failed to set machine quantity.');
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, sourceKey, scenarioKey],
  );

  const handlePlaceFirstMachine = useCallback(
    async (taskKey: string, machineKey: string) => {
      // FEAT-2026-0007 fix: place into the active draft scenario, not BASELINE.
      await placeTaskOnMachine(taskKey, machineKey, scenarioKey ?? undefined);
      if (source && sourceKey) {
        const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
        setTasks(data);
      }
      bumpGrid();
      emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, sourceKey, scenarioKey],
  );

  const handleDelete = useCallback(
    async (taskKey: string) => {
      await deleteTask(taskKey);
      setTasks((prev) => prev.filter((t) => t.ScheduleTaskKey !== taskKey));
      bumpGrid();
      emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // "+ Add Task" — opens the Add Task dialog (FEAT-2026-0007). The old behaviour
  // re-generated from the source, which added nothing once every line item already
  // had a task ("0 task(s) added"). Now the planner picks a TaskType and adds one
  // task; refetch the table + grid after it's created.
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const handleTaskCreated = useCallback(async () => {
    if (!source || !sourceKey) return;
    const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
    setTasks(data);
    bumpGrid();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, sourceKey, scenarioKey]);

  // ── Bulk mode helpers ───────────────────────────────────────────────────────

  const handleToggleBulkMode = () => {
    setBulkMode((v) => !v);
    setSelectedKeys(new Set());
  };

  const handleToggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedKeys(
      selectedKeys.size === tasks.length
        ? new Set()
        : new Set(tasks.map((t) => t.ScheduleTaskKey)),
    );
  };

  // Flash a booking bar in the grid — red for conflicts (6B.6), primary sage
  // when a task name is clicked in the top table (item-4 UX).
  const flashBooking = useFlashBooking(gridContainerRef);

  // Reload tasks + grid after an auto-schedule re-plan / undo. Declared BEFORE the
  // no-scenario guard below so the hook order stays stable (Rules of Hooks).
  const handleAnchorApplied = useCallback(async () => {
    await loadTasks();
    bumpGrid();
  // bumpGrid is a stable closure; loadTasks is memoized on source/sourceKey/scenarioKey.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTasks]);

  // Earliest start across the scenario's bookings — the auto-schedule default date.
  const earliestStart = useMemo(() => {
    const starts = tasks.map((t) => t.StartDate).filter((s): s is string => Boolean(s)).sort();
    return starts[0] ?? null;
  }, [tasks]);

  // Stat-holiday window spanning the tasks — so the Duration column counts WORKING
  // days only (weekends were already excluded; this adds holidays). Same server
  // source (region-aware, NB default) the Machine Schedule grid shades with.
  const holWindow = useMemo(() => {
    const starts = tasks.map((t) => t.StartDate).filter((s): s is string => Boolean(s)).sort();
    const ends = tasks.map((t) => t.EndDate).filter((s): s is string => Boolean(s)).sort();
    const today = new Date().toISOString().slice(0, 10);
    return { start: starts[0] ?? today, end: ends[ends.length - 1] ?? today };
  }, [tasks]);
  const { holidays: holidayRows } = useScheduleHolidays(holWindow.start, holWindow.end);
  const holidayDates = useMemo(
    () => holidayRows.map((h) => h.date.slice(0, 10)),
    [holidayRows],
  );

  // ── Prep consolidation (BUG-2026-0165, 2026-07-26): fold per-line prep rows
  // into one aggregate entry per prep step. DISPLAY-only — the member tasks and
  // bookings are untouched, and bulk mode keeps the flat 1:1 list so the
  // selection checkboxes stay per-task.
  const taskEntries = useMemo(
    () => (bulkMode
      ? tasks.map((task) => ({ kind: 'task' as const, task }))
      : rollupPrepTasks(tasks)),
    [tasks, bulkMode],
  );
  const [expandedPrepGroups, setExpandedPrepGroups] = useState<Set<string>>(new Set());
  const togglePrepGroup = useCallback((key: string) => {
    setExpandedPrepGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  // Flash EVERY machine bar for a task (BUG fix: a 2-machine task used to flash
  // only the first booking). Used by both single rows and the group header.
  const flashAllForTask = useCallback(
    (task: TaskWithBookings) => {
      for (const b of task.Bookings) flashBooking(b.MachineBookingKey, theme.palette.primary.main);
    },
    [flashBooking, theme.palette.primary.main],
  );

  // ── No scenario guard ──────────────────────────────────────────────────────

  if (!scenario || !source || !sourceKey) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 5,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <PrecisionManufacturingIcon sx={{ fontSize: '2.5rem', color: 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          Machine planning
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          Select or create a scenario above to start planning machine assignments.
        </Typography>
      </Box>
    );
  }

  const unplacedCount = tasks.filter((t) => t.Bookings.length === 0).length;

  // A scheduled (Submitted) / archived plan is locked — editing is refused server-side
  // (scenarioEditGuard), so grey the edit surface out and explain why (2026-06-25).
  const locked = scenario.Status === 'Submitted' || scenario.Status === 'Archived';
  // Dim + block interaction on the editable body when locked (a clear read-only state
  // without threading `disabled` into every cell control).
  const lockedBodySx = locked
    ? { opacity: 0.55, pointerEvents: 'none' as const, filter: 'grayscale(0.25)' }
    : {};

  // One task's row — a task split across ≥2 machines renders as a grouped
  // header + one sub-row per machine (independent dates/drag); single-machine
  // and unplaced tasks keep the compact single-row layout. Shared by the flat
  // list and the expanded members of a consolidated prep step.
  const renderTaskRow = (task: TaskWithBookings, rowNumber: number | string) =>
    task.Bookings.length > 1 ? (
      <Phase1TaskGroup
        key={task.ScheduleTaskKey}
        task={task}
        rowNumber={rowNumber}
        bulkMode={bulkMode}
        selected={selectedKeys.has(task.ScheduleTaskKey)}
        holidays={holidayDates}
        onToggleSelect={handleToggleRow}
        onUpdateMachineCount={handleUpdateMachineCount}
        onChangeMachine={handleChangeMachine}
        onChangeBookingStart={handleChangeBookingStart}
        onChangeBookingQty={handleChangeBookingQty}
        onDelete={handleDelete}
        onActivateAll={() => flashAllForTask(task)}
        onActivateBooking={(bk) => flashBooking(bk, theme.palette.primary.main)}
      />
    ) : (
      <Phase1TaskRow
        key={task.ScheduleTaskKey}
        task={task}
        rowNumber={rowNumber}
        bulkMode={bulkMode}
        selected={selectedKeys.has(task.ScheduleTaskKey)}
        holidays={holidayDates}
        onToggleSelect={handleToggleRow}
        onUpdateMachineCount={handleUpdateMachineCount}
        onChangeMachine={handleChangeMachine}
        onChangeBookingStart={handleChangeBookingStart}
        onPlaceFirstMachine={handlePlaceFirstMachine}
        onDelete={handleDelete}
        onActivate={() => flashAllForTask(task)}
        conflictSuggestedStart={conflictSuggestionByTask.get(task.ScheduleTaskKey) ?? null}
      />
    );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

      {/* Read-only banner when the plan is scheduled. */}
      {locked && (
        <Alert
          severity="info"
          icon={<LockOutlinedIcon fontSize="inherit" />}
          sx={{ '& .MuiAlert-message': { fontSize: '0.82rem' } }}
        >
          This plan is <strong>{scenario.Status === 'Archived' ? 'archived' : 'scheduled'}</strong> — machines, dates and splits are read-only.
          Use <strong>Reopen to Edit</strong> (top of the page) to make changes, or <strong>Duplicate</strong> to plan a variation.
        </Alert>
      )}

      {/* ── Section header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <PrecisionManufacturingIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Project Tasks (Scenario)
        </Typography>
        <Box sx={{ flex: 1 }} />

        {tasks.length > 0 && (
          <Chip
            label={`${tasks.length} task${tasks.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              fontSize: '0.72rem',
            }}
          />
        )}
        {unplacedCount > 0 && (
          <Tooltip title="Some tasks have no machine assigned — use the Assigned Machine(s) dropdown.">
            <Chip
              label={`${unplacedCount} unplaced`}
              size="small"
              sx={{
                backgroundColor: alpha(theme.schedule.conflictSoft, 0.12),
                color: theme.schedule.conflictSoft,
                fontSize: '0.72rem',
              }}
            />
          </Tooltip>
        )}
        {/* G-4: sticky "not schedulable" warning — source tests with no catalog
            mapping never became tasks. Re-checks on every gridRefreshKey bump. */}
        {source && sourceKey && (
          <UnmappedTestsChip source={source} sourceKey={sourceKey} refreshKey={gridRefreshKey} />
        )}

        {/* Auto-schedule: Project Start Date + strategy toggle + Set (with Undo).
            Replaces the old slide-only "Project Start Date" + destructive
            "Master start date" — Set re-plans per the chosen strategy. */}
        {tasks.length > 0 && (
          <AutoScheduleControl
            source={source}
            sourceKey={sourceKey}
            scenarioKey={scenarioKey}
            currentStart={earliestStart}
            disabled={loading || locked || !source || !sourceKey}
            onApplied={handleAnchorApplied}
            onFlash={setSaveFlash}
            onError={setError}
          />
        )}

        {/* Edit Multiple toggle */}
        <Button
          size="small"
          variant={bulkMode ? 'contained' : 'outlined'}
          startIcon={<EditIcon fontSize="small" />}
          onClick={handleToggleBulkMode}
          disabled={locked}
          sx={{ textTransform: 'none', fontSize: '0.78rem' }}
        >
          Edit Multiple
        </Button>

        {/* + Add Task */}
        <Tooltip title="Add a new task to this scenario">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setAddTaskOpen(true)}
              disabled={loading || locked}
              sx={{ textTransform: 'none', fontSize: '0.78rem' }}
            >
              + Add Task
            </Button>
          </span>
        </Tooltip>

        {/* Refresh */}
        <Tooltip title="Refresh task list">
          <span>
            <Button
              size="small"
              startIcon={
                loading
                  ? <CircularProgress size={14} />
                  : <RefreshIcon fontSize="small" />
              }
              onClick={() => { void loadTasks(); }}
              disabled={loading}
              sx={{ fontSize: '0.78rem' }}
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Divider />

      {/* ── Save flash banner ──────────────────────────────────────────────── */}
      {saveFlash && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          onClose={() => setSaveFlash(null)}
        >
          {saveFlash}
        </Alert>
      )}

      {/* ── Error banner ───────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading spinner ────────────────────────────────────────────────── */}
      {loading && tasks.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {!loading && tasks.length === 0 && !error && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 1.5,
          }}
        >
          <PrecisionManufacturingIcon
            sx={{ fontSize: 48, opacity: 0.3, color: theme.schedule.machineTime }}
          />
          <Typography variant="body1" fontWeight={500} color="text.secondary">
            {hasIndexTests ? 'No machine planning needed' : 'No tasks yet'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', maxWidth: 340 }}
          >
            {hasIndexTests ? (
              <>
                This plan only has index tests — they&rsquo;re planned in{' '}
                <strong>Phase 2 — Index tests</strong>. Use <strong>+ Add Task</strong> if
                you need a machine-based task.
              </>
            ) : (
              <>
                Tasks are generated automatically from the{' '}
                {source === 'Quote' ? 'quote' : 'work order'}. Use{' '}
                <strong>+ Add Task</strong> to add one manually.
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* ── Task table ─────────────────────────────────────────────────────── */}
      {tasks.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${theme.border.color}`,
            borderRadius: `${theme.border.radius}px`,
            // 'clip' (not 'hidden') keeps the rounded-corner clipping WITHOUT
            // becoming a scroll container — the sticky header below must pin
            // against the window scroll (sticky-headers UX pass, Option 1).
            overflow: 'clip',
            backgroundColor: theme.palette.background.paper,
            ...lockedBodySx,
          }}
        >
          {/* stickyHeader pins the <th> row while the page scrolls, releasing
              when the table ends (native hand-off). The Schedule section
              scrolls in SchedulePage's content column (NOT the window), and
              sticky pins to that column's CONTENT edge — offset by its padding
              so the header docks flush at the column's visible top instead of
              24px low with rows leaking above it. */}
          <Table
            size="small"
            stickyHeader
            sx={{
              '& .MuiTableCell-stickyHeader': {
                top: theme.spacing(-SCHEDULE_CONTENT_PADDING),
              },
            }}
          >
            <TableHead>
              <TableRow>
                {/* Select-all checkbox for Edit Multiple mode */}
                {bulkMode && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      backgroundColor: theme.table.headerBg,
                      borderBottom: `2px solid ${theme.border.color}`,
                    }}
                  >
                    <Checkbox
                      size="small"
                      indeterminate={
                        selectedKeys.size > 0 && selectedKeys.size < tasks.length
                      }
                      checked={selectedKeys.size === tasks.length && tasks.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                {PHASE1_TASK_COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align ?? 'left'}
                    sx={{
                      width: col.width,
                      backgroundColor: theme.table.headerBg,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: 0.3,
                      py: 1,
                      px: 1.5,
                      borderBottom: `2px solid ${theme.border.color}`,
                    }}
                  >
                    {col.tooltip ? (
                      <Tooltip title={col.tooltip} placement="top">
                        <span>{col.label}</span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {taskEntries.map((entry, idx) => {
                // Consolidated prep step (BUG-2026-0165): one aggregate header
                // row; expanding reveals the real member rows (sub-numbered)
                // with every per-line action intact. Bulk mode renders FLAT
                // (taskEntries falls back to 1:1) so row selection stays simple.
                if (entry.kind === 'prepGroup') {
                  const g = entry.group;
                  const open = expandedPrepGroups.has(g.groupKey);
                  return (
                    <React.Fragment key={g.groupKey}>
                      <Phase1PrepGroupRow
                        group={g}
                        rowNumber={idx + 1}
                        holidays={holidayDates}
                        expanded={open}
                        onToggle={() => togglePrepGroup(g.groupKey)}
                      />
                      {open && g.members.map((task, j) => renderTaskRow(task, `${idx + 1}.${j + 1}`))}
                    </React.Fragment>
                  );
                }
                return renderTaskRow(entry.task, idx + 1);
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ── Bulk action bar (6F.4) ─────────────────────────────────────────── */}
      {bulkMode && selectedKeys.size > 0 && (
        <Phase1BulkActionBar
          selectedTasks={tasks.filter((t) => selectedKeys.has(t.ScheduleTaskKey))}
          scenarioKey={scenarioKey}
          onDone={() => {
            setSelectedKeys(new Set());
            void loadTasks();
            bumpGrid();
            emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
          }}
        />
      )}

      {/* ── Footer tip ─────────────────────────────────────────────────────── */}
      {tasks.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: 'flex-end', pb: 1 }}
        >
          Tip: Edit Start to push a task to a later date — End follows automatically.
          Changing Machine Qty distributes the total duration across parallel machines.
        </Typography>
      )}

      {/* ── Phase-1 machine daily grid (6B.5) ─────────────────────────────── */}
      {/* onConflictsChange + gridContainerRef wired for 6B.6 conflicts panel. */}
      {/* onPlacementCommitted (BUG-2026-0105): a successful in-grid drag reloads
          the top task table so its Start/End date + Assigned Machine(s) reflect
          the bar's new position — the symmetric counterpart to bumpGrid(), which
          already refreshes the grid after a table edit. */}
      <Phase1MachineGrid
        tasks={tasks}
        scenarioKey={scenarioKey}
        refreshKey={gridRefreshKey}
        compareToBaseline={compareToBaseline}
        onConflictsChange={setConflicts}
        gridContainerRef={gridContainerRef}
        onPlacementCommitted={() => {
          void loadTasks();
          // In-grid drag-reschedule is also a booking-date change — broadcast it so
          // the availability cache + Phase-2 + the live views refresh (Gigi, 2026-06-22).
          emitScheduleChange({ kind: 'bookings-changed', source: 'scenario-phase1' });
        }}
      />

      {/* ── Conflicts panel (6B.6) ─────────────────────────────────────────── */}
      {/* Scenario-scoped: scenarioKey passed to fetchScheduleConflicts in the grid. */}
      <SchedulingConflictsPanel
        conflicts={conflicts}
        onJumpToBooking={(bk) => flashBooking(bk, theme.schedule.conflictHard)}
      />

      {/* ── Add Task dialog (FEAT-2026-0007 "+ Add Task") ──────────────────── */}
      {source && sourceKey && (
        <AddTaskDialog
          open={addTaskOpen}
          source={source}
          sourceKey={sourceKey}
          onClose={() => setAddTaskOpen(false)}
          onCreated={() => { void handleTaskCreated(); }}
        />
      )}
    </Box>
  );
};
